<script setup lang="ts">
import { useStore } from 'vuex';
import AiRobot, { CustomDislikeReason, DislikeReasonList } from '@edm-dts-components/ai-robot';
import { IChatContainerProps, Answer } from '@edm-dts-components/ai-robot';
import { computed, onBeforeMount, reactive, ref, shallowRef } from 'vue';
import { fetchllmFeedback, fetchllmPromptTemplate } from '@/api/search';
import { SSE } from 'sse.js';
import { getUuid } from '@/utils/crypto';
import { removeStroage } from '@/utils/storage';
import { jumpLogin, removeToken } from '@/utils/system';
import { marked } from 'marked';

interface IPromptQuestion {
  id: number;
  subject: string;
  promptTemplate: string;
  status: string;
  createTime: string;
  createUser: string;
  updateTime: null | string;
  updateUser: string;
  deleteFlag: number;
}

const aiRobotRef = shallowRef<InstanceType<typeof AiRobot>>();
const store = useStore();
const chatList = reactive<IChatContainerProps['chatList']>([]);
let pageNum = 2;
const pageCount = 3;
let allPromptQuestions: IPromptQuestion[] = [];
const promptQuestions = ref<IPromptQuestion[]>([]);
const topicUuid = getUuid();
let generateLoading = false;
let sseInstance: SSE | null = null;
const baseApiUrl: string | undefined = import.meta.env.VITE_GLOB_API_URL;
const token = computed(() => {
  return store.getters.token;
});

const getPromptQuestions = async () => {
  allPromptQuestions = await fetchllmPromptTemplate();
  promptQuestions.value = allPromptQuestions.slice(0, pageCount);
};

const onRefreshQuestion = () => {
  if ((pageNum - 1) * pageCount >= allPromptQuestions.length) {
    pageNum = 1;
  }
  promptQuestions.value = allPromptQuestions.slice((pageNum - 1) * pageCount, pageNum * pageCount);
  pageNum++;
};

const fetchAImodle = async (message: string, answer: Answer, originContent: string, scrollToBottom: () => void) => {
  try {
    const payload = {
      prompt: message,
      topic: topicUuid,
      dialogueSource: 'EDM',
    };
    const sse = new SSE(`${baseApiUrl}llm/chat`, {
      headers: {
        'X-Token': token.value,
        'Content-Type': 'application/JSON',
      },
      method: 'POST',
      payload: JSON.stringify(payload),
    });
    sseInstance = sse;
    const xhr = sse.xhr;
    if (xhr) {
      xhr.onreadystatechange = () => {
        try {
          if (xhr.readyState === 4) {
            const response = JSON.parse(xhr.response || '{}');
            if (generateLoading) {
              // 手动停止也会进入，通过判断是否还在生成中决定是否修改content
              answer.content = response?.msg || 'LLM is busy, please try again later.';
            }
            scrollToBottom();
          }
        } catch (e) {
          // 这是正常情况
        }
      };
    }

    sse.addEventListener('answer', function (e: any) {
      try {
        const data = JSON.parse(e.data || '{}');
        if (data?.answer || data?.hyperlink) {
          if (data.answer) {
            originContent += data.answer;
            answer.content = marked(originContent) as string;
            answer.id = e.id;
          }
          if (data.hyperlink) {
            answer.link = data.hyperlink.map((link: string) => {
              return location.origin + '/#' + link;
            });
          }
          scrollToBottom();
        } else {
          const res = JSON.parse(sse.xhr?.response || '{}');
          if (['401', '403', '405'].includes(res.status)) {
            removeStroage('userInfo');
            removeToken();
            // 跳转登录
            const didloginUrl = import.meta.env.VITE_GLOB_DID_URL;
            jumpLogin(didloginUrl);
          }
        }
      } catch (error) {
        console.log(error);
        console.log(e);
        console.log(sse);
      }
    });

    sse.addEventListener('readystatechange', (e: any) => {
      if (e.readyState === 2) {
        generateLoading = false;
        answer.isLoading = false;
        sse.close();
        sseInstance = null;
      }
    });

    sse.stream();
  } catch (e) {
    console.log(e);
  }
};

const onSendMessage = async (message: string, scrollToBottom: () => void) => {
  if (generateLoading) {
    window.$message.warning('Please watting, the message is generating right now');
    return;
  }
  chatList.push({
    type: 'question',
    content: message,
  });
  // 调用接口生成答案
  const answer = reactive<Answer>({
    id: '',
    type: 'answer',
    content: '',
    link: [],
    isLoading: true,
    feedback: null,
    isAIGenerated: true,
  });
  chatList.push(answer);
  let originContent = '';
  generateLoading = true;
  fetchAImodle(message, answer, originContent, scrollToBottom);
  scrollToBottom();
};

const onStopReply = () => {
  if (sseInstance) {
    sseInstance.close();
    sseInstance = null;
    generateLoading = false;
  }
};

const onFeedBack = async (isLike: boolean, answer: Answer, scrollToBottom: () => void) => {
  if (isLike) {
    answer.feedback = 'like';
    fetchllmFeedback({
      content: '',
      type: 'like',
      uuid: answer.id,
    });
  } else {
    answer.feedback = 'dislike';
    chatList.push({
      type: 'dislikeReasonList',
      content: 'Could you please tell me the reason for your dissatisfaction?',
      reasonOptions: [
        'Misunderstanding of the question',
        'Inadequate response',
        'Presence of sensitive / harmful information',
        'Other',
      ],
      done: false,
      answer,
    });
    scrollToBottom();
  }
};

const onClickDislikeReasonList = (reason: string, dislikeReasonList: DislikeReasonList, scrollToBottom: () => void) => {
  dislikeReasonList.done = true;
  if (reason === 'Other') {
    chatList.push({
      type: 'customDislikeReason',
      content: 'Please enter the reason',
      reason: '',
      done: false,
      answer: dislikeReasonList.answer,
    });
    scrollToBottom();
  } else {
    fetchllmFeedback({
      content: reason,
      type: 'dislike',
      uuid: dislikeReasonList.answer.id,
    });
  }
};

const onSubmitDislike = (customDislikeReason: CustomDislikeReason) => {
  fetchllmFeedback({
    content: customDislikeReason.reason,
    type: 'dislike',
    uuid: customDislikeReason.answer.id,
  });
  customDislikeReason.done = true;
};

const closeOnClickDocument = () => {
  document.addEventListener('click', () => {
    aiRobotRef.value?.closeChat();
  });
};

onBeforeMount(() => {
  closeOnClickDocument();
});
</script>

<template>
  <AiRobot
    ref="aiRobotRef"
    class="custom-ai-robot"
    :ratio="10"
    :use-popover="true"
    popover-text="DTO Assistant"
    prompt-key="promptTemplate"
    welcome-text="Hello! How can I assist you today?"
    :chat-list="chatList"
    :prompt-questions="promptQuestions"
    :username="store.getters.name"
    @show.once="getPromptQuestions"
    @refresh-qustion="onRefreshQuestion"
    @send-message="onSendMessage"
    @stop-reply="onStopReply"
    @feedback="onFeedBack"
    @click-dislike-reason-list="onClickDislikeReasonList"
    @submit-dislike="onSubmitDislike"
  >
    <template #frame-img>
      <img class="assisant-frame-img" src="@/assets/frame-img.png" alt="" />
    </template>
    <template #head-img>
      <img src="@/assets/edm_loading.png" alt="logo" />
    </template>
  </AiRobot>
</template>

<style lang="scss">
#assistant-bot.custom-ai-robot {
  right: 24px;
  bottom: 53px;
  z-index: 1000;
  .chat_icon {
    height: 60px;
    & > div {
      position: relative;
    }
    .expand {
      width: 26px;
      height: 13px;
      top: 45%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(0);
    }
    .da {
      // 设计稿切图不对称 后续需要标准化
      width: 35.5px;
      height: 26px;
      top: 50%;
      left: 54%;
      transform: translate(-50%, -50%) rotate(0);
    }
    .expand,
    .da {
      transform-origin: center;
      &.hide {
        transform: translate(-50%, -50%) rotate(-60deg);
      }
    }
    .assisant-frame-img {
      // width: 110px;
      width: 60px;
    }
  }

  .assistant_container {
    bottom: 72px;
    right: 24px;
  }
}
</style>
